import React, { useState } from 'react';
import { 
  Box, Container, Typography, Paper, Tabs, Tab, Chip, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert
} from '@mui/material';
import { API_CONFIG, API_ENDPOINTS } from '../config';

const ApiDocs: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  const apiUrl = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CAPTURE_SCREENSHOT}`;

  const codeBlocks = {
    quickstart: `curl -X POST ${apiUrl} \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://example.com",
    "viewport": {
      "width": 1920,
      "height": 1080
    },
    "format": "png"
  }'`,
    quickstartHtml: `curl -X POST ${apiUrl} \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "html": "PCFET0NUWVBFIGh0bWw+PGh0bWw+PGhlYWQ+PHRpdGxlPkhlbGxvPC90aXRsZT48L2hlYWQ+PGJvZHk+PGgxPkhlbGxvIFdvcmxkPC9oMT48L2JvZHk+PC9odG1sPg==",
    "viewport": {
      "width": 1920,
      "height": 1080
    },
    "format": "png"
  }'`,
    response: `{
  "success": true,
  "screenshot_url": "https://cdn.screenshot.app/abc123.png",
  "width": 1920,
  "height": 1080,
  "file_size": 245678
}`,
    javascript: `const axios = require('axios');

// Example 1: Capture from URL
const captureFromUrl = async () => {
  const response = await axios.post(
    '${apiUrl}',
    {
      url: 'https://example.com',
      viewport: { width: 1920, height: 1080 },
      format: 'png'
    },
    {
      headers: {
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json'
      }
    }
  );
  console.log(response.data.screenshot_url);
};

// Example 2: Capture from HTML (base64 encoded)
const captureFromHtml = async () => {
  const html = '<!DOCTYPE html><html><head><title>Hello</title></head><body><h1>Hello World</h1></body></html>';
  const base64Html = Buffer.from(html).toString('base64');
  
  const response = await axios.post(
    '${apiUrl}',
    {
      html: base64Html,
      viewport: { width: 1920, height: 1080 },
      format: 'png'
    },
    {
      headers: {
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json'
      }
    }
  );
  console.log(response.data.screenshot_url);
};`,
    python: `import requests
import base64

# Example 1: Capture from URL
def capture_from_url():
    response = requests.post(
        '${apiUrl}',
        json={
            'url': 'https://example.com',
            'viewport': {'width': 1920, 'height': 1080},
            'format': 'png'
        },
        headers={
            'Authorization': 'Bearer YOUR_API_KEY',
            'Content-Type': 'application/json'
        }
    )
    print(response.json()['screenshot_url'])

# Example 2: Capture from HTML (base64 encoded)
def capture_from_html():
    html = '<!DOCTYPE html><html><head><title>Hello</title></head><body><h1>Hello World</h1></body></html>'
    base64_html = base64.b64encode(html.encode('utf-8')).decode('utf-8')
    
    response = requests.post(
        '${apiUrl}',
        json={
            'html': base64_html,
            'viewport': {'width': 1920, 'height': 1080},
            'format': 'png'
        },
        headers={
            'Authorization': 'Bearer YOUR_API_KEY',
            'Content-Type': 'application/json'
        }
    )
    print(response.json()['screenshot_url'])`
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 64px - 300px)' }}>
      <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
        <Typography variant="h3" gutterBottom fontWeight={700} sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
          API Documentation
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Complete guide to integrating with our screenshot API
        </Typography>

        {/* Layout: Content on left, Tabs on right */}
        <Box sx={{ 
          display: 'flex', 
          gap: 3,
          flexDirection: { xs: 'column', md: 'row' }
        }}>
          {/* Main Content Area */}
          <Box sx={{ flex: 1, minWidth: 0 }}>

        {activeTab === 0 && (
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Quick Start Guide
            </Typography>
            <Typography variant="body1" paragraph>
              Get started with our Screenshot API in just a few minutes.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              1. Get Your API Key
            </Typography>
            <Typography variant="body1" paragraph>
              Sign up for an account and retrieve your API key from the dashboard.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              2. Make Your First Request
            </Typography>
            
            <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
              Option A: Capture from URL
            </Typography>
            <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 3, borderRadius: 2, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.9rem' }}>
              <pre style={{ margin: 0 }}>{codeBlocks.quickstart}</pre>
            </Box>

            <Typography variant="body2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
              Option B: Capture from HTML (Base64 Encoded)
            </Typography>
            <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 3, borderRadius: 2, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.9rem', mb: 2 }}>
              <pre style={{ margin: 0 }}>{codeBlocks.quickstartHtml}</pre>
            </Box>
            <Alert severity="info">
              The <code>html</code> parameter contains base64 encoded HTML. The example above decodes to: 
              <code style={{ display: 'block', marginTop: '8px', padding: '8px', background: '#f5f5f5' }}>
                {'<!DOCTYPE html><html><head><title>Hello</title></head><body><h1>Hello World</h1></body></html>'}
              </code>
            </Alert>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              3. Receive Your Screenshot
            </Typography>
            <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 3, borderRadius: 2, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.9rem' }}>
              <pre style={{ margin: 0 }}>{codeBlocks.response}</pre>
            </Box>
          </Paper>
        )}

        {activeTab === 1 && (
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Authentication
            </Typography>
            <Typography variant="body1" paragraph>
              All API requests require authentication using your API key.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              API Key Authentication
            </Typography>
            <Typography variant="body1" paragraph>
              Include your API key in the Authorization header:
            </Typography>
            <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 3, borderRadius: 2, fontFamily: 'monospace' }}>
              Authorization: Bearer YOUR_API_KEY
            </Box>

            <Alert severity="warning" sx={{ mt: 3 }}>
              <strong>Keep Your API Key Secure</strong><br />
              Never expose your API key in client-side code or public repositories.
            </Alert>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Rate Limits
            </Typography>
            <Typography variant="body1" paragraph>
              Rate limits vary by plan:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li><strong>Free:</strong> 10 requests per minute</li>
              <li><strong>Pro:</strong> 100 requests per minute</li>
              <li><strong>Enterprise:</strong> Custom limits</li>
            </Box>
          </Paper>
        )}

        {activeTab === 2 && (
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              API Endpoint
            </Typography>
            <Typography variant="body1" paragraph color="text.secondary">
              Our API provides a single endpoint for capturing screenshots.
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
              <Box sx={{ border: '2px solid #000', borderRadius: 2, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Chip label="POST" color="success" size="small" />
                  <Typography variant="body1" fontFamily="monospace" fontWeight={600} fontSize="1.1rem">
                    {API_ENDPOINTS.CAPTURE_SCREENSHOT}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Capture a screenshot from a URL or HTML content.
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  This endpoint requires either <strong>url</strong> or <strong>html</strong> parameter (not both).
                </Alert>
              </Box>
            </Box>
          </Paper>
        )}

        {activeTab === 3 && (
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Request Parameters
            </Typography>

            <Alert severity="warning" sx={{ my: 3 }}>
              <strong>Important:</strong> You must provide either <code>url</code> OR <code>html</code> parameter, but not both.
            </Alert>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Required Parameters (One of the following)
            </Typography>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <TableContainer>
                <Table sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Parameter</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell><code>url</code></TableCell>
                      <TableCell>string</TableCell>
                      <TableCell>The URL of the webpage to capture</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><code>html</code></TableCell>
                      <TableCell>string</TableCell>
                      <TableCell><strong>Base64 encoded</strong> HTML content to render and capture</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Optional Parameters
            </Typography>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <TableContainer>
                <Table sx={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Parameter</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { param: 'viewport.width', type: 'integer', desc: 'Width in pixels (default: 1920)' },
                      { param: 'viewport.height', type: 'integer', desc: 'Height in pixels (default: 1080)' },
                      { param: 'format', type: 'string', desc: 'Output format: png, jpg, webp (default: png)' },
                      { param: 'quality', type: 'integer', desc: 'Quality from 1-100 (default: 90)' },
                      { param: 'full_page', type: 'boolean', desc: 'Capture full page (default: false)' },
                      { param: 'delay', type: 'integer', desc: 'Wait time in ms before capture (default: 0)' },
                    ].map((row, index) => (
                      <TableRow key={index}>
                        <TableCell><code>{row.param}</code></TableCell>
                        <TableCell>{row.type}</TableCell>
                        <TableCell>{row.desc}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        )}

        {activeTab === 4 && (
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Code Examples
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              JavaScript / Node.js
            </Typography>
            <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 3, borderRadius: 2, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.9rem' }}>
              <pre style={{ margin: 0 }}>{codeBlocks.javascript}</pre>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Python
            </Typography>
            <Box sx={{ bgcolor: '#1e1e1e', color: '#d4d4d4', p: 3, borderRadius: 2, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.9rem' }}>
              <pre style={{ margin: 0 }}>{codeBlocks.python}</pre>
            </Box>
          </Paper>
        )}

        {activeTab === 5 && (
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Error Codes
            </Typography>
            <Typography variant="body1" paragraph>
              The API uses standard HTTP status codes and returns detailed error messages.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Status Codes
            </Typography>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <TableContainer>
                <Table sx={{ minWidth: 500 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Code</strong></TableCell>
                      <TableCell><strong>Meaning</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { code: '200', meaning: 'Success' },
                      { code: '400', meaning: 'Bad Request - Invalid parameters' },
                      { code: '401', meaning: 'Unauthorized - Invalid API key' },
                      { code: '429', meaning: 'Too Many Requests - Rate limit exceeded' },
                      { code: '500', meaning: 'Internal Server Error' },
                    ].map((row, index) => (
                      <TableRow key={index}>
                        <TableCell><code>{row.code}</code></TableCell>
                        <TableCell>{row.meaning}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        )}
          </Box>

          {/* Vertical Tabs on Right Side */}
          <Box sx={{ 
            width: { xs: '100%', md: '250px' },
            flexShrink: 0
          }}>
            <Paper sx={{ position: 'sticky', top: 80, overflow: 'hidden' }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                orientation="vertical"
                variant="scrollable"
                sx={{
                  '& .MuiTab-root': {
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    minHeight: 48,
                    px: 3,
                    py: 2,
                    borderBottom: '1px solid #e0e0e0',
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  },
                  '& .Mui-selected': {
                    bgcolor: 'black',
                    color: 'white !important',
                    fontWeight: 600
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none'
                  }
                }}
              >
                <Tab label="Quick Start" />
                <Tab label="Authentication" />
                <Tab label="Endpoint" />
                <Tab label="Parameters" />
                <Tab label="Code Examples" />
                <Tab label="Error Codes" />
              </Tabs>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ApiDocs;
